import { Activity, useEffect, useRef, useSyncExternalStore, type MouseEvent } from 'react'
import { Router, RouterProvider, createPath, type Navigator } from 'react-router-dom'
import { Header, WorkspaceHistory } from '../components/Header'
import { PageTabs } from '../components/PageTabs'
import { Toaster } from '../components/toasts'
import { APP_NAME } from '../components/useDocumentTitle'
import { tellShell } from '../lib/shell'
import { PageBoundary } from '../components/layout'
import { StoreProvider } from '../store/context'
import { TabContext, TabOwnerContext } from './context'
import { WorkspaceModel, type WorkspacePane, type WorkspaceTab } from './model'
import { command, safePath } from './protocol'
import './workspace.css'

function followLink(event: MouseEvent<HTMLDivElement>, model: WorkspaceModel, tab: WorkspaceTab, modifiedOnly = false) {
  if (event.defaultPrevented || event.button !== 0 && event.button !== 1 || !(event.target instanceof Element)) return
  const anchor = event.target.closest<HTMLAnchorElement>('a[href]')
  if (!anchor || anchor.hasAttribute('download')) return
  const modified = event.button === 1 || event.metaKey || event.ctrlKey || anchor.target === '_blank'
  if (modifiedOnly && !modified) return
  let url: URL
  try { url = new URL(anchor.getAttribute('href')!, new URL(tab.path, window.location.origin)) } catch { return }
  if (url.origin !== window.location.origin) return
  const path = safePath(url.pathname + url.search + url.hash)
  if (!path) return // Engine links retain their native/browser handling.
  if (modified) {
    event.preventDefault(); event.stopPropagation()
    model.open(path, event.shiftKey ? 'foreground' : 'background', tab.id)
  } else if (!event.altKey && !event.shiftKey) {
    event.preventDefault()
    void tab.router.navigate(path)
  }
}

/** Pane chrome has its own stable Router context; navigation is forwarded to the selected tab's data router. */
function PaneChrome({ model, pane, index }: { model: WorkspaceModel; pane: WorkspacePane; index: number }) {
  const tab = model.tabs.get(pane.front)!
  const navigator: Navigator = {
    createHref: to => typeof to === 'string' ? to : createPath(to),
    encodeLocation: to => { const url = new URL(typeof to === 'string' ? to : createPath(to), window.location.origin); return { pathname: url.pathname, search: url.search, hash: url.hash } },
    go: step => { model.usePane(index); void tab.router.navigate(step) },
    push: (to, state) => { model.usePane(index); void tab.router.navigate(to, { state }) },
    replace: (to, state) => { model.usePane(index); void tab.router.navigate(to, { state, replace: true }) },
  }
  return <div className="workspace-chrome" data-workspace-chrome-tab={tab.id} data-pane-trailing={index === 1 || undefined} style={model.panes.length === 2 ? { left: index === 0 ? 0 : `${model.share! * 100}%`, width: `${(index === 0 ? model.share! : 1 - model.share!) * 100}%` } : undefined}
    onPointerDownCapture={() => { model.rememberFocus(); model.used(tab.id); model.usePane(index); tellShell({ workspaceContext: { tab: tab.id } }) }} onFocusCapture={event => { model.usePane(index); if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) tellShell({ workspaceContext: { tab: tab.id } }) }} onKeyDownCapture={() => model.used(tab.id)}
    onContextMenuCapture={() => tellShell({ workspaceContext: { tab: tab.id } })}
    onClickCapture={event => followLink(event, model, tab, true)} onAuxClickCapture={event => followLink(event, model, tab, true)} onClick={event => followLink(event, model, tab)}>
    <StoreProvider store={tab.store}>
      <TabContext.Provider value={tab.scope}>
        <Router location={tab.router.state.location} navigator={navigator}>
          <WorkspaceHistory.Provider value={{ back: tab.position > 0, forward: tab.position + 1 < tab.entries.length, go: navigator.go }}>
            <Header onSearch={() => { model.usePane(index); window.dispatchEvent(new CustomEvent('poulpe-workspace-search', { detail: tab.id })) }} />
            <PageTabs workspace={{ dragging: model.dragged !== null, tabs: pane.tabs.map(id => {
              const item = model.tabs.get(id)!
              return { id, title: item.title, active: pane.front === id, href: new URL(item.path, window.location.origin).href }
            }), request: message => model.tabRequest(index, message) }} />
          </WorkspaceHistory.Provider>
        </Router>
      </TabContext.Provider>
    </StoreProvider>
  </div>
}

function TabView({ model, tab }: { model: WorkspaceModel; tab: WorkspaceTab }) {
  const index = model.paneOf(tab.id)
  const visible = model.panes[index]?.front === tab.id
  const owner = model.current === tab
  const root = useRef<HTMLDivElement>(null)
  // React disconnects descendant effects while hidden. Capture the focus before
  // hiding, then restore the actual retained field and its native selection.
  useEffect(() => {
    if (!owner || !visible || !tab.focus) return
    const target = tab.focus.isConnected ? tab.focus : tab.focus.classList.contains('cm-content') ? root.current?.querySelectorAll<HTMLElement>('.cm-content')[Math.max(0, tab.editorFocus)] : null
    if (!target || !root.current?.contains(target)) return
    target.focus({ preventScroll: true })
    if (tab.selection && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) target.setSelectionRange(tab.selection.start, tab.selection.end, tab.selection.direction)
  }, [owner, visible, tab])
  return <div ref={root} className={`workspace-tab${owner ? ' is-owner' : ''}`} data-workspace-tab={tab.id} hidden={!visible}
    style={model.panes.length === 2 ? { left: index === 0 ? 0 : `${model.share! * 100}%`, width: `${(index === 0 ? model.share! : 1 - model.share!) * 100}%` } : undefined}
    onPointerDownCapture={() => { model.used(tab.id); model.usePane(index); tellShell({ workspaceContext: { tab: tab.id } }) }} onFocusCapture={event => { model.usePane(index); if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) tellShell({ workspaceContext: { tab: tab.id } }) }} onKeyDownCapture={() => model.used(tab.id)}
    onBlurCapture={event => { if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) model.rememberFocus(event.target) }}
    onClickCapture={event => followLink(event, model, tab, true)} onAuxClickCapture={event => followLink(event, model, tab, true)} onClick={event => followLink(event, model, tab)}
    onContextMenuCapture={() => tellShell({ workspaceContext: { tab: tab.id } })}>
    <TabContext.Provider value={tab.scope}>
      <TabOwnerContext.Provider value={owner}>
      <StoreProvider store={tab.store}>
        <Activity mode={visible ? 'visible' : 'hidden'}>
          <PageBoundary resetKey={`${tab.path}:${tab.reload}`} onReload={tab.scope.reload}>
            <RouterProvider key={tab.reload} router={tab.router} />
          </PageBoundary>
        </Activity>
      </StoreProvider>
      </TabOwnerContext.Provider>
    </TabContext.Provider>
  </div>
}

export function Workspace({ model }: { model: WorkspaceModel }) {
  const revision = useSyncExternalStore(model.subscribe, model.snapshot)
  const root = useRef<HTMLDivElement>(null)
  const ready = useRef(false)
  const lifetime = useRef(0)
  const mouseResize = useRef<AbortController | null>(null)
  useEffect(() => () => mouseResize.current?.abort(), [model.panes.length])
  useEffect(() => {
    if (model.panes.length !== 2 || !root.current) return
    const measure = () => { const width = root.current?.getBoundingClientRect().width; if (width) model.resize(model.share ?? .5, width) }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(root.current)
    return () => observer.disconnect()
  }, [model, model.panes.length])
  useEffect(() => {
    const generation = ++lifetime.current
    const heard = (event: Event) => {
      const value = command((event as CustomEvent<unknown>).detail)
      if (value) model.dispatch(value)
    }
    window.addEventListener('poulpe-workspace-command', heard)
    // Listeners precede the handshake: native may synchronously release its queue.
    tellShell({ workspaceState: model.state() })
    if (!ready.current) { ready.current = true; tellShell({ workspaceReady: true }) }
    return () => {
      window.removeEventListener('poulpe-workspace-command', heard)
      // StrictMode immediately reconnects its effects. Only a real workspace
      // removal (welcome/signout) invalidates native readiness and recovery.
      queueMicrotask(() => {
        if (lifetime.current !== generation) return
        tellShell({ workspaceUnavailable: true })
        model.clearRecovery()
      })
    }
  }, [model])
  useEffect(() => {
    tellShell({ workspaceState: model.state() })
    tellShell({ workspaceRecovery: model.recovery() })
    document.title = model.current?.title && model.current.title !== APP_NAME ? `${model.current.title} · ${APP_NAME}` : APP_NAME
  }, [model, revision])
  const resize = (clientX: number) => {
    const box = root.current?.getBoundingClientRect()
    if (box?.width) model.resize((clientX - box.left) / box.width, box.width)
  }
  return <div className="workspace" ref={root}>
    {model.panes.map((pane, index) => <PaneChrome key={pane.id} model={model} pane={pane} index={index} />)}
    {[...model.tabs.values()].map(tab => <TabView key={tab.id} model={model} tab={tab} />)}
    {model.panes.length === 2 && <div className="workspace-divider" role="separator" aria-orientation="vertical" aria-valuemin={10} aria-valuemax={90} aria-valuenow={Math.round(model.share! * 100)} tabIndex={0} style={{ left: `${model.share! * 100}%` }}
      onKeyDown={event => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); model.resize(model.share! + (event.key === 'ArrowLeft' ? -.02 : .02), root.current?.getBoundingClientRect().width) } }}
      onPointerDown={event => {
        if (event.button !== 0) return
        mouseResize.current?.abort()
        event.preventDefault(); event.currentTarget.focus({ preventScroll: true })
        event.currentTarget.setPointerCapture(event.pointerId); resize(event.clientX)
      }}
      onPointerMove={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) resize(event.clientX) }}
      onPointerUp={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId) }}
      onMouseDown={event => {
        if (event.button !== 0) return
        // WebKit can deliver mousedown without pointerdown after an HTML tab
        // drag. Keep that real mouse gesture usable without assuming capture.
        event.preventDefault()
        event.currentTarget.focus({ preventScroll: true })
        mouseResize.current?.abort()
        const drag = new AbortController()
        mouseResize.current = drag
        const stop = () => { drag.abort(); if (mouseResize.current === drag) mouseResize.current = null }
        window.addEventListener('mousemove', event => { if ((event.buttons & 1) === 0) stop(); else resize(event.clientX) }, { signal: drag.signal })
        window.addEventListener('mouseup', stop, { signal: drag.signal })
        window.addEventListener('blur', stop, { signal: drag.signal })
        resize(event.clientX)
      }} />}
    <Toaster />
  </div>
}
